import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Filter, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useQuote } from '../context/QuoteContext';
import { productService, categoryService } from '../services/api';
import { toast } from '../hooks/use-toast';

// Product image component with fallback
const ProductImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Generate a consistent color based on product name
  const getColorFromName = (name) => {
    const colors = [
      'from-pink-400 to-pink-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-purple-400 to-purple-600',
      'from-orange-400 to-orange-600',
      'from-teal-400 to-teal-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  if (hasError || !src) {
    return (
      <div className={`${className} bg-gradient-to-br ${getColorFromName(alt)} flex items-center justify-center`}>
        <div className="text-center text-white p-4">
          <Package className="h-12 w-12 mx-auto mb-2 opacity-80" />
          <p className="text-xs font-medium opacity-90 line-clamp-2">{alt}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative bg-gray-100`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
          <div className="animate-pulse text-gray-400">
            <Package className="h-8 w-8" />
          </div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`w-full h-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        onError={() => setHasError(true)}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

const ProductList = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const subcategory = searchParams.get('sub');
  const { addToQuote } = useQuote();
  const [products, setProducts] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let data = await productService.getAllProducts(category);
        
        // Filter by subcategory if specified
        if (subcategory) {
          data = data.filter(p => p.subcategory === subcategory);
        }
        
        setProducts(data);
        
        if (category && category !== 'all') {
          const categories = await categoryService.getAllCategories();
          const cat = categories.find(c => c.category_id === category);
          setCurrentCategory(cat);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, subcategory]);

  const sortedProducts = [...products].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'new') return (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0);
    return 0;
  });

  const handleQuickAdd = (product) => {
    addToQuote(product, 1);
    toast({
      title: 'Added to Quote Request',
      description: `${product.name} added to your quote.`,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-pink-900 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">
            {subcategory 
              ? subcategory.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
              : currentCategory ? currentCategory.name : 'All Products'}
          </h1>
          <p className="text-pink-100">
            {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''} available
            {subcategory && currentCategory && (
              <span className="ml-2">
                in <Link to={`/products/${category}`} className="underline hover:text-white">{currentCategory.name}</Link>
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded-md px-4 py-2"
            >
              <option value="name">Sort by Name</option>
              <option value="new">New First</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-600">
            Need help? <Link to="/contact" className="text-pink-600 hover:underline">Contact us</Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedProducts.map((product) => (
            <Card key={product.product_code} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col">
              <CardContent className="p-6 flex flex-col h-full">
                <Link to={`/product/${product.product_code}`}>
                  <div className="relative mb-4">
                    <ProductImage
                      src={product.local_image ? `https://procloudpromo.com${product.local_image}` : product.image_url}
                      alt={product.name}
                      className="aspect-square rounded-lg overflow-hidden"
                    />
                    {product.is_new && (
                      <Badge className="absolute top-2 left-2 bg-pink-600">New</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-lg mb-1 group-hover:text-pink-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">{product.product_code}</p>
                  <p className="text-sm text-gray-700 line-clamp-2 mb-3">
                    {product.description}
                  </p>
                </Link>
                <div className="mt-auto space-y-2">
                  <div className="flex gap-2">
                    <Link to={`/product/${product.product_code}`} className="flex-1">
                      <Button variant="outline" className="w-full">
                        View Details
                      </Button>
                    </Link>
                    <Button
                      onClick={() => handleQuickAdd(product)}
                      className="flex-1"
                    >
                      Add to Quote
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {sortedProducts.length === 0 && (
          <div className="text-center py-16">
            <h3 className="text-2xl font-semibold text-gray-700 mb-4">No products found</h3>
            <Link to="/products/all">
              <Button>
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
